import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Bell, Clock, Send, Loader2, Sparkles, Info, Users, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";

interface NotificationSetting {
  id: string;
  setting_key: string;
  is_enabled: boolean;
  recipient_email: string | null;
  recipient_name: string | null;
  send_time: string;
}

interface AdminUser {
  id: string;
  email: string;
  display_name?: string;
}

interface NotificationSettingsProps {
  establishmentId?: string;
}

export function NotificationSettings({ establishmentId }: NotificationSettingsProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dailySummarySettings, setDailySummarySettings] = useState<NotificationSetting | null>(null);
  const [newResSettings, setNewResSettings] = useState<NotificationSetting | null>(null);
  const [cancelSettings, setCancelSettings] = useState<NotificationSetting | null>(null);
  const [modifSettings, setModifSettings] = useState<NotificationSetting | null>(null);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  
  const [selectedAdminEmail, setSelectedAdminEmail] = useState<string>('');
  const [sendTime, setSendTime] = useState('07:00');
  const [newResEnabled, setNewResEnabled] = useState(false);
  const [includeCustomerName, setIncludeCustomerName] = useState(false);
  const [dailySummaryEnabled, setDailySummaryEnabled] = useState(false);
  const [cancelEnabled, setCancelEnabled] = useState(false);
  const [modifEnabled, setModifEnabled] = useState(false);

  useEffect(() => {
    fetchData();
  }, [establishmentId]);

  const fetchData = async () => {
    await Promise.all([fetchSettings(), fetchAdminUsers()]);
  };

  const fetchSettings = async () => {
    if (!establishmentId) {
      setLoading(false);
      return;
    }
    
    try {
      // Fetch daily summary settings
      const { data: dailyData, error: dailyError } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('establishment_id', establishmentId)
        .eq('setting_key', 'daily_summary_email')
        .maybeSingle();

      if (!dailyError && dailyData) {
        setDailySummarySettings(dailyData);
        setDailySummaryEnabled(dailyData.is_enabled);
        setSendTime(dailyData.send_time?.substring(0, 5) || '07:00');
        if (dailyData.recipient_email) {
          setSelectedAdminEmail(dailyData.recipient_email);
        }
      } else if (!dailyData) {
        // Create missing daily summary setting
        const { data: newDaily, error: createError } = await supabase
          .from('notification_settings')
          .insert({
            establishment_id: establishmentId,
            setting_key: 'daily_summary_email',
            is_enabled: false,
            send_time: '07:00:00'
          })
          .select()
          .single();
        
        if (!createError && newDaily) {
          setDailySummarySettings(newDaily);
          setDailySummaryEnabled(newDaily.is_enabled);
          setSendTime('07:00');
        }
      }

      // Fetch new reservation notification settings
      const { data: newResData, error: newResError } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('establishment_id', establishmentId)
        .eq('setting_key', 'new_reservation_notification')
        .maybeSingle();

      if (!newResError && newResData) {
        setNewResSettings(newResData);
        setNewResEnabled(newResData.is_enabled);
        setIncludeCustomerName((newResData as any).include_customer_name ?? false);
        // If we didn't get an email from daily summary, try from new res
        if (!selectedAdminEmail && newResData.recipient_email) {
          setSelectedAdminEmail(newResData.recipient_email);
        }
      } else if (!newResData) {
        // Create missing new reservation notification setting
        const { data: newRes, error: createError } = await supabase
          .from('notification_settings')
          .insert({
            establishment_id: establishmentId,
            setting_key: 'new_reservation_notification',
            is_enabled: false,
            send_time: '00:00:00'
          })
          .select()
          .single();
        
        if (!createError && newRes) {
          setNewResSettings(newRes);
          setNewResEnabled(newRes.is_enabled);
        }
      }

      // Fetch cancellation notification settings
      const { data: cancelData } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('establishment_id', establishmentId)
        .eq('setting_key', 'cancellation_notification')
        .maybeSingle();

      if (cancelData) {
        setCancelSettings(cancelData);
        setCancelEnabled(cancelData.is_enabled);
      } else {
        const { data: newCancel } = await supabase
          .from('notification_settings')
          .insert({
            establishment_id: establishmentId,
            setting_key: 'cancellation_notification',
            is_enabled: false,
            send_time: '00:00:00'
          })
          .select()
          .single();
        if (newCancel) {
          setCancelSettings(newCancel);
          setCancelEnabled(newCancel.is_enabled);
        }
      }

      // Fetch modification notification settings
      const { data: modifData } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('establishment_id', establishmentId)
        .eq('setting_key', 'modification_notification')
        .maybeSingle();

      if (modifData) {
        setModifSettings(modifData);
        setModifEnabled(modifData.is_enabled);
      } else {
        const { data: newModif } = await supabase
          .from('notification_settings')
          .insert({
            establishment_id: establishmentId,
            setting_key: 'modification_notification',
            is_enabled: false,
            send_time: '00:00:00'
          })
          .select()
          .single();
        if (newModif) {
          setModifSettings(newModif);
          setModifEnabled(newModif.is_enabled);
        }
      }
    } catch (error) {
      console.error('Erreur chargement settings:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les paramètres de notification.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminUsers = async () => {
    if (!establishmentId) {
      setLoadingAdmins(false);
      return;
    }

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        setLoadingAdmins(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('manage-users', {
        body: { action: 'list' },
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });

      if (error) throw error;

      if (data?.users) {
        // Filter to only include admin users
        const admins = data.users
          .filter((user: any) => user.role === 'admin')
          .map((user: any) => ({
            id: user.id,
            email: user.email,
            display_name: user.display_name || user.email
          }));
        setAdminUsers(admins);
      }
    } catch (error) {
      console.error('Erreur chargement admins:', error);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleAdminChange = async (email: string) => {
    setSelectedAdminEmail(email);
    setSaving(true);

    try {
      // Update both settings with the new email
      const updates = [];

      if (dailySummarySettings) {
        updates.push(
          supabase
            .from('notification_settings')
            .update({ recipient_email: email })
            .eq('id', dailySummarySettings.id)
        );
      }

      if (newResSettings) {
        updates.push(
          supabase
            .from('notification_settings')
            .update({ recipient_email: email })
            .eq('id', newResSettings.id)
        );
      }

      if (cancelSettings) {
        updates.push(
          supabase
            .from('notification_settings')
            .update({ recipient_email: email })
            .eq('id', cancelSettings.id)
        );
      }

      if (modifSettings) {
        updates.push(
          supabase
            .from('notification_settings')
            .update({ recipient_email: email })
            .eq('id', modifSettings.id)
        );
      }

      await Promise.all(updates);

      toast({
        title: "Destinataire mis à jour",
        description: "Les notifications seront envoyées à " + email
      });
    } catch (error) {
      console.error('Erreur mise à jour destinataire:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le destinataire.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleNewRes = async (checked: boolean) => {
    if (!newResSettings) return;
    
    if (checked && !selectedAdminEmail) {
      toast({
        title: "Destinataire requis",
        description: "Veuillez d'abord sélectionner un administrateur destinataire.",
        variant: "destructive"
      });
      return;
    }

    setNewResEnabled(checked);
    setSaving(true);

    try {
      // When enabling, also ensure recipient_email is set
      const updateData: { is_enabled: boolean; recipient_email?: string } = { is_enabled: checked };
      if (checked && selectedAdminEmail) {
        updateData.recipient_email = selectedAdminEmail;
      }

      const { error } = await supabase
        .from('notification_settings')
        .update(updateData)
        .eq('id', newResSettings.id);

      if (error) throw error;

      // Update local state
      if (checked && selectedAdminEmail) {
        setNewResSettings({ ...newResSettings, recipient_email: selectedAdminEmail });
      }

      toast({
        title: checked ? "Notifications activées" : "Notifications désactivées",
        description: checked 
          ? "Vous recevrez un email à chaque nouvelle réservation."
          : "Les notifications de nouvelle réservation sont désactivées."
      });
    } catch (error) {
      console.error('Erreur toggle new res:', error);
      setNewResEnabled(!checked);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le paramètre.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCancel = async (checked: boolean) => {
    if (!cancelSettings) return;
    if (checked && !selectedAdminEmail) {
      toast({ title: "Destinataire requis", description: "Veuillez d'abord sélectionner un administrateur destinataire.", variant: "destructive" });
      return;
    }
    setCancelEnabled(checked);
    setSaving(true);
    try {
      const updateData: { is_enabled: boolean; recipient_email?: string } = { is_enabled: checked };
      if (checked && selectedAdminEmail) updateData.recipient_email = selectedAdminEmail;
      const { error } = await supabase.from('notification_settings').update(updateData).eq('id', cancelSettings.id);
      if (error) throw error;
      if (checked && selectedAdminEmail) setCancelSettings({ ...cancelSettings, recipient_email: selectedAdminEmail });
      toast({ title: checked ? "Notifications activées" : "Notifications désactivées", description: checked ? "Vous recevrez un email à chaque annulation de réservation." : "Les notifications d'annulation sont désactivées." });
    } catch (error) {
      console.error('Erreur toggle cancel:', error);
      setCancelEnabled(!checked);
      toast({ title: "Erreur", description: "Impossible de modifier le paramètre.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleModif = async (checked: boolean) => {
    if (!modifSettings) return;
    if (checked && !selectedAdminEmail) {
      toast({ title: "Destinataire requis", description: "Veuillez d'abord sélectionner un administrateur destinataire.", variant: "destructive" });
      return;
    }
    setModifEnabled(checked);
    setSaving(true);
    try {
      const updateData: { is_enabled: boolean; recipient_email?: string } = { is_enabled: checked };
      if (checked && selectedAdminEmail) updateData.recipient_email = selectedAdminEmail;
      const { error } = await supabase.from('notification_settings').update(updateData).eq('id', modifSettings.id);
      if (error) throw error;
      if (checked && selectedAdminEmail) setModifSettings({ ...modifSettings, recipient_email: selectedAdminEmail });
      toast({ title: checked ? "Notifications activées" : "Notifications désactivées", description: checked ? "Vous recevrez un email à chaque modification de réservation." : "Les notifications de modification sont désactivées." });
    } catch (error) {
      console.error('Erreur toggle modif:', error);
      setModifEnabled(!checked);
      toast({ title: "Erreur", description: "Impossible de modifier le paramètre.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDailySummary = async (checked: boolean) => {
    if (!dailySummarySettings) return;
    
    if (checked && !selectedAdminEmail) {
      toast({
        title: "Destinataire requis",
        description: "Veuillez d'abord sélectionner un administrateur destinataire.",
        variant: "destructive"
      });
      return;
    }

    setDailySummaryEnabled(checked);
    setSaving(true);

    try {
      // When enabling, also ensure recipient_email is set
      const updateData: { is_enabled: boolean; recipient_email?: string } = { is_enabled: checked };
      if (checked && selectedAdminEmail) {
        updateData.recipient_email = selectedAdminEmail;
      }

      const { error } = await supabase
        .from('notification_settings')
        .update(updateData)
        .eq('id', dailySummarySettings.id);

      if (error) throw error;

      // Update local state
      if (checked && selectedAdminEmail) {
        setDailySummarySettings({ ...dailySummarySettings, recipient_email: selectedAdminEmail });
      }

      toast({
        title: checked ? "Récapitulatif activé" : "Récapitulatif désactivé",
        description: checked 
          ? "Vous recevrez un récapitulatif quotidien."
          : "Le récapitulatif quotidien est désactivé."
      });
    } catch (error) {
      console.error('Erreur toggle daily summary:', error);
      setDailySummaryEnabled(!checked);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le paramètre.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTimeChange = async (newTime: string) => {
    if (!dailySummarySettings) return;
    
    setSendTime(newTime);
    setSaving(true);

    try {
      const { error } = await supabase
        .from('notification_settings')
        .update({ send_time: newTime + ':00' })
        .eq('id', dailySummarySettings.id);

      if (error) throw error;

      toast({
        title: "Heure mise à jour",
        description: `Le récapitulatif sera envoyé à ${newTime}.`
      });
    } catch (error) {
      console.error('Erreur mise à jour heure:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier l'heure d'envoi.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!selectedAdminEmail) {
      toast({
        title: "Destinataire requis",
        description: "Veuillez sélectionner un administrateur destinataire.",
        variant: "destructive"
      });
      return;
    }

    setTesting(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-daily-summary', {
        body: { test: true, establishmentId }
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: "Email envoyé",
          description: data.message || "Le récapitulatif test a été envoyé."
        });
      } else {
        toast({
          title: "Information",
          description: data.message || "Aucune réservation aujourd'hui."
        });
      }
    } catch (error: any) {
      console.error('Erreur test email:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'envoyer l'email test.",
        variant: "destructive"
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading || loadingAdmins) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const hasNoAdmins = adminUsers.length === 0;

  return (
    <div className="space-y-6">
      {/* Destinataire section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            <CardTitle>Destinataire des notifications</CardTitle>
          </div>
          <CardDescription>
            Toutes les notifications sont envoyées à un administrateur de l'établissement.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Email partagé</AlertTitle>
            <AlertDescription>
              L'email du destinataire est le même pour toutes les notifications (nouvelle réservation, annulation, modification et récapitulatif quotidien). 
              Pour ajouter ou modifier les administrateurs, rendez-vous dans la gestion des utilisateurs.
            </AlertDescription>
          </Alert>

          {hasNoAdmins ? (
            <Alert variant="destructive">
              <AlertTitle>Aucun administrateur trouvé</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>Vous devez avoir au moins un administrateur pour recevoir les notifications.</p>
                <Link to="/dashboard/users" className="inline-flex items-center gap-1 text-primary hover:underline font-medium">
                  Gérer les utilisateurs <ExternalLink className="w-3 h-3" />
                </Link>
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-3">
              <Label>Administrateur destinataire</Label>
              <Select value={selectedAdminEmail} onValueChange={handleAdminChange} disabled={saving}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Sélectionner un administrateur" />
                </SelectTrigger>
                <SelectContent>
                  {adminUsers.map((admin) => (
                    <SelectItem key={admin.id} value={admin.email}>
                      {admin.display_name || admin.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Link to="/dashboard/users" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  Gérer les utilisateurs <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification nouvelle réservation */}
      <Card className={hasNoAdmins ? "opacity-60" : ""}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <CardTitle>Notification nouvelle réservation</CardTitle>
          </div>
          <CardDescription>
            Recevez un email instantané à chaque nouvelle réservation effectuée.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="newres-enabled">Activer les notifications</Label>
              <p className="text-sm text-muted-foreground">
                Un email sera envoyé immédiatement à chaque nouvelle réservation.
              </p>
            </div>
            <Switch
              id="newres-enabled"
              checked={newResEnabled}
              onCheckedChange={handleToggleNewRes}
              disabled={!newResSettings || hasNoAdmins || saving}
            />
          </div>

          {newResEnabled && (
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="space-y-0.5">
                <Label htmlFor="include-customer-name">Inclure le nom du client</Label>
                <p className="text-sm text-muted-foreground">
                  Le prénom et nom du client seront affichés dans l'email de notification.
                </p>
              </div>
              <Switch
                id="include-customer-name"
                checked={includeCustomerName}
                onCheckedChange={async (checked) => {
                  if (!newResSettings) return;
                  setIncludeCustomerName(checked);
                  setSaving(true);
                  try {
                    const { error } = await supabase
                      .from('notification_settings')
                      .update({ include_customer_name: checked } as any)
                      .eq('id', newResSettings.id);
                    if (error) throw error;
                    toast({
                      title: checked ? "Nom du client inclus" : "Nom du client masqué",
                      description: checked 
                        ? "Le nom du client apparaîtra dans les notifications." 
                        : "Le nom du client ne sera plus affiché."
                    });
                  } catch (error) {
                    console.error('Erreur toggle include customer name:', error);
                    setIncludeCustomerName(!checked);
                    toast({ title: "Erreur", description: "Impossible de modifier le paramètre.", variant: "destructive" });
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={!newResSettings || hasNoAdmins || saving}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification annulation */}
      <Card className={hasNoAdmins ? "opacity-60" : ""}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-destructive" />
            <CardTitle>Notification d'annulation</CardTitle>
          </div>
          <CardDescription>
            Recevez un email lorsqu'un client annule sa réservation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="cancel-enabled">Activer les notifications</Label>
              <p className="text-sm text-muted-foreground">
                Un email sera envoyé immédiatement à chaque annulation.
              </p>
            </div>
            <Switch
              id="cancel-enabled"
              checked={cancelEnabled}
              onCheckedChange={handleToggleCancel}
              disabled={!cancelSettings || hasNoAdmins || saving}
            />
          </div>
        </CardContent>
      </Card>

      {/* Notification modification */}
      <Card className={hasNoAdmins ? "opacity-60" : ""}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-500" />
            <CardTitle>Notification de modification</CardTitle>
          </div>
          <CardDescription>
            Recevez un email lorsqu'un client modifie sa réservation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="modif-enabled">Activer les notifications</Label>
              <p className="text-sm text-muted-foreground">
                Un email sera envoyé immédiatement à chaque modification.
              </p>
            </div>
            <Switch
              id="modif-enabled"
              checked={modifEnabled}
              onCheckedChange={handleToggleModif}
              disabled={!modifSettings || hasNoAdmins || saving}
            />
          </div>
        </CardContent>
      </Card>

      {/* Récapitulatif quotidien */}
      <Card className={hasNoAdmins ? "opacity-60" : ""}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            <CardTitle>Récapitulatif quotidien</CardTitle>
          </div>
          <CardDescription>
            Recevez chaque matin un email récapitulatif des réservations du jour.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Toggle activation */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="daily-enabled">Activer le récapitulatif</Label>
              <p className="text-sm text-muted-foreground">
                Un email sera envoyé chaque matin s'il y a des réservations.
              </p>
            </div>
            <Switch
              id="daily-enabled"
              checked={dailySummaryEnabled}
              onCheckedChange={handleToggleDailySummary}
              disabled={!dailySummarySettings || hasNoAdmins || saving}
            />
          </div>

          {/* Heure d'envoi */}
          <div className="space-y-2">
            <Label htmlFor="time" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Heure d'envoi
            </Label>
            <Input
              id="time"
              type="time"
              value={sendTime}
              onChange={(e) => handleSendTimeChange(e.target.value)}
              className="w-32"
              disabled={hasNoAdmins || saving}
            />
            <p className="text-xs text-muted-foreground">
              L'email sera envoyé à cette heure chaque jour (heure du serveur).
            </p>
          </div>

          <div className="pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={handleTestEmail}
              disabled={testing || !selectedAdminEmail || hasNoAdmins}
            >
              {testing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Tester l'envoi
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info automatique */}
      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Clock className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h4 className="font-medium">Planification automatique</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Les modifications sont enregistrées automatiquement. 
                Les notifications de nouvelle réservation sont instantanées, le récapitulatif est envoyé à l'heure configurée.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
